import * as React from "react"
import { CheckIcon, CaretDownIcon} from "@phosphor-icons/react"

import { cn } from "@/lib/utils"
import { Button } from "@/shadcn/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shadcn/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn/components/ui/popover"

export interface ComboboxOption {
  value: string
  label: string
}

export interface ComboboxProps {
  options: ComboboxOption[]
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  className?: string
  disabled?: boolean
  showSearch?: boolean
  showBorderOnTrigger?: boolean
  ariaLabel?: string
}

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = "Select option...",
  searchPlaceholder = "Search...",
  emptyText = "No option found.",
  className,
  disabled = false,
  showSearch = false,
  showBorderOnTrigger = true,
  ariaLabel,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)

  const selectedOption = options.find((option) => option.value === value)

  // Helper function to capitalize first character
  const capitalizeFirst = (text: string) => {
    if (!text) return text
    return text.charAt(0).toUpperCase() + text.slice(1)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel || placeholder}
          disabled={disabled}
          className={cn(
            "w-[200px] justify-between label-m-medium text-fg-neutral-strong rounded-[8px]",
            showBorderOnTrigger 
              ? "border border-border-token-brand-minimal bg-bg-brand-secondary-default hover:bg-bg-brand-secondary-hover" 
              : "!border-0 !bg-transparent hover:!bg-transparent",
            className
          )}
        >
          {selectedOption ? capitalizeFirst(selectedOption.label) : placeholder}
          <CaretDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[1000] w-[200px] p-0 label-m-medium text-fg-neutral-strong bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-[8px]">
        <Command>
          {showSearch && <CommandInput placeholder={searchPlaceholder} />}
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option, index) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={(currentValue) => {
                    onValueChange?.(currentValue === value ? "" : currentValue)
                    setOpen(false)
                  }}
                  className={cn(
                    "flex items-center justify-between h-[48px] cursor-pointer",
                    index < options.length - 1 && "border-b border-border-token-neutral-minimal",
                    "hover:bg-bg-brand-secondary-hover active:bg-bg-brand-secondary-active"
                  )}
                >
                  <span className="label-m-medium text-fg-neutral-strong">{capitalizeFirst(option.label)}</span>
                  <CheckIcon
                    className={cn(
                      "h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
